import { useEffect, useState } from 'react';
import { PastEvent, fetchPastEvents } from '../services/pastEventService';
import { resolveImageUrl } from '../utils/imageUrl';

export default function PastEvents() {
  const [pastEvents, setPastEvents] = useState<PastEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Standaard fallback afbeelding
  const defaultImage = '/images/aikido-social.jpg';

  // Functie om datum in formaat DD-MM-YYYY of andere Nederlandse datum om te zetten naar Date object
  const parseDutchDate = (dateStr: string): Date => {
    try {
      // Check voor formaat DD maand YYYY (bijv. "16 februari 2025")
      const dutchMonths = [
        'januari', 'februari', 'maart', 'april', 'mei', 'juni',
        'juli', 'augustus', 'september', 'oktober', 'november', 'december'
      ];
      
      // Als het een datum met maandnaam is
      for (let i = 0; i < dutchMonths.length; i++) {
        if (dateStr.toLowerCase().includes(dutchMonths[i])) {
          const parts = dateStr.split(' ');
          const day = parseInt(parts[0]);
          const month = i; // Index van de maand (0-11)
          const year = parseInt(parts[2]);
          return new Date(year, month, day);
        }
      }
      
      // Check voor formaat DD-MM-YYYY
      if (dateStr.includes('-')) {
        const [day, month, year] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      
      // Als geen specifiek formaat herkend wordt, probeer standaard parsing
      return new Date(dateStr);
    } catch (error) {
      console.error('Fout bij het parsen van datum:', dateStr, error);
      // Fallback op huidige datum (deze events komen dan achteraan in de sortering)
      return new Date(0); // 1 januari 1970
    }
  };
  
  useEffect(() => {
    const loadPastEvents = async () => {
      try {
        console.log('PastEvents: Starting to fetch past events');
        const events = await fetchPastEvents();
        
        // Debug informatie voor het Ivan Marinovic event
        const ivanEvent = events.find(e => 
          e.title.toLowerCase().includes('ivan') || 
          e.title.toLowerCase().includes('marinovic')
        );
        
        if (ivanEvent) {
          console.log('FOUND IVAN EVENT:', ivanEvent);
          console.log('IMAGE URL:', ivanEvent.image);
        } else {
          console.log('NO IVAN EVENT FOUND IN:', events.map(e => e.title));
        }
        
        // Sorteer de evenementen op datum (nieuwste eerst)
        const sortedEvents = [...events].sort((a, b) => {
          const dateA = parseDutchDate(a.date);
          const dateB = parseDutchDate(b.date);
          return dateB.getTime() - dateA.getTime(); // Omgekeerde volgorde (nieuwste eerst)
        });
        
        console.log('PastEvents: Sorted events (newest first):', sortedEvents);
        setPastEvents(sortedEvents);
        setError(null);
      } catch (err: unknown) {
        const errorObj = err as Error;
        const errorMessage = errorObj.message || 'Er is een probleem opgetreden bij het laden van de evenementen.';
        setError(errorMessage);
        console.error('Error loading past events:', errorObj);
      } finally {
        setLoading(false);
      }
    };

    loadPastEvents();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
        <p>Laden...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-10">
      <h1 className="text-4xl font-bold mb-8">Eerdere Evenementen</h1>
      
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {pastEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="h-52 w-full overflow-hidden">
              <img
                src={resolveImageUrl(event.image, defaultImage)}
                alt={event.title}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onError={(e) => {
                  console.error('Afbeelding kon niet worden geladen:', event.image);
                  const target = e.target as HTMLImageElement;
                  target.src = defaultImage;
                }}
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
              <div className="flex flex-wrap mb-3">
                <span className="text-gray-600 mr-4">{event.date}</span>
                {event.startTime && (
                  <span className="text-gray-600 mr-4">
                    {event.startTime}{event.endTime ? ` - ${event.endTime}` : ''} uur
                  </span>
                )}
                {event.category && (
                  <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                    {event.category}
                  </span>
                )}
              </div>
              
              {event.teacher && (
                <p className="text-gray-600 mb-2">
                  <span className="font-medium">Docent:</span> {event.teacher}
                </p>
              )}
              
              <p className="text-gray-600 mb-4">
                <span className="font-medium">Locatie:</span> {event.location}
              </p>
              
              <div className="text-gray-700 mb-4">
                {event.description}
              </div>
              
              <div className="flex flex-wrap gap-2 mt-4">
                {event.eventLink && (
                  <a 
                    href={event.eventLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-300"
                  >
                    Meer informatie
                  </a>
                )}
                
                {event.registrationLink && (
                  <a 
                    href={event.registrationLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-300"
                  >
                    Inschrijven
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
